<template>
  <div class="h-100 p-2">
    <div v-if="isInitialized && !isSaving" class="h-100">
      <div class="d-flex justify-content-end mb-2">
        <b-button-toolbar>
          <b-button-group>
            <b-button variant="primary" :pressed="true" class="mr-2">
              <em class="fas fa-info" /> Статус:
              {{ content.contentObject.status }}
            </b-button>
            <error-list-button :scopedErrors="errors" class="mr-2" :key="errorKey" />
            <div class="mr-2" v-b-tooltip :title="switchFileTooltip">
              <b-button
                variant="outline-primary"
                @click="onSaveClick()"
                :disabled="!canSave || !this.toggleValidation || this.isContentSaved"
              >
                <em class="far fa-1x fa-save"></em> Сохранить
              </b-button>
            </div>
            <b-button variant="outline-primary" @click="onClose">
              <em class="fas fa1-x fa-times-circle" /> Назад
            </b-button>
          </b-button-group>
        </b-button-toolbar>
      </div>
      <b-alert class="mt-2" variant="primary" :show="isZipping">
        <div class="d-flex align-items-center">
          <b-spinner class="mr-3" variant="primary" />
          <strong>Упаковка...</strong>
        </div>
      </b-alert>
      <b-form inline class="mb-2">
        <div class="mr-2" v-b-tooltip :title="switchFileTooltip">
          <b-form-select
            @change="onFileChange"
            v-model="selectedFile"
            class="mr-2"
            :disabled="!canSave || !this.toggleValidation"
          >
            <optgroup
              v-for="[group, groupFiles] in Object.entries(files)"
              :key="group"
              :label="group"
            >
              <option
                v-for="file in Object.keys(groupFiles)"
                :key="file"
                :value="{ group, file }"
                >{{ group }}: {{ file }}</option
              >
            </optgroup>
          </b-form-select>
        </div>
        <b-form-select v-model="selectedTheme" class="mr-2">
          <option value="vs">Светлая тема</option>
          <option value="vs-dark">Тёмная тема</option>
        </b-form-select>
        <b-form-checkbox v-model="toggleValidation" class="mr-2" switch>
          Валидация
        </b-form-checkbox>
        <b-button variant="outline-primary" @click="onImport" class="mr-2">
          <em class="fas fa-file-import"></em> Импорт
        </b-button>
        <b-button variant="outline-primary" @click="onExport" class="mr-2">
          <em class="fas fa-file-export"></em> Экспорт
        </b-button>
        <b-button variant="outline-primary" @click="onZip" class="mr-2">
          <em class="fas fa-file-archive"></em> Экспорт ZIP
        </b-button>
        <b-button
          variant="outline-primary"
          class="mr-2"
          v-b-modal.resourceModal
        >
          Ресурсы
        </b-button>
      </b-form>
      <MonacoWrapper
        class="w-100"
        style="height: 60vh"
        :language="currentLanguage"
        v-model="code"
        :theme="selectedTheme"
        ref="monacoWrapper"
        @editorMounted="setUpJson"
        @glyphClicked="onGlyphClicked"
      />
      <ErrorList
        v-if="toggleValidation"
        :errors="localErrors"
        @errorClicked="onErrorClicked"
      />
      <UploadCodeModal @fileUploaded="onFileUploaded" />
      <ResourceModal />
      <ComponentEditModal
        :elementId="elementEditId"
        @updateElement="updateFile"
      />
    </div>
    <div v-else class="d-flex justify-content-center mt-5">
      <b-spinner
        variant="primary"
        style="width: 5cm; height: 5cm;"
        label="Загрузка..."
      ></b-spinner>
    </div>
  </div>
</template>

<script>
/* eslint-disable no-plusplus */
import { mapGetters, mapMutations, mapActions } from 'vuex'
import _ from 'lodash'
import jsonMap from 'json-source-map'
import { saveAs } from 'file-saver'

import MonacoWrapper from '@/components/content/education/training/tools/MonacoWrapper.vue'
import ErrorListButton from '@/components/content/education/training/tools/ErrorListButton.vue'
import ErrorList from '@/components/content/education/training/tools/ErrorList.vue'
import UploadCodeModal from '@/components/content/education/training/modals/UploadCodeModal.vue'
import ResourceModal from '@/components/content/education/training/modals/ResourceModal.vue'
import ComponentEditModal from '@/components/content/education/training/modals/ComponentModal.vue'
import RouteMixin from '@/components/helpers/mixins/route-mixin'

export default {
  components: {
    MonacoWrapper,
    ErrorListButton,
    ErrorList,
    UploadCodeModal,
    ResourceModal,
    ComponentEditModal
  },
  mixins: [RouteMixin],
  data() {
    return {
      selectedFile: {
        file: 'JSON',
        group: 'Курс'
      },
      code: '',
      selectedTheme: this.$cookies.get('editorTheme') || 'vs',
      localErrors: [],
      toggleValidation: true,
      jsonMap: null,
      commitParams: {},
      isSaving: false,
      isZipping: false,
      jsonData: null,
      elementEditId: 0,
      errorKey: 0
    }
  },
  created() {
    if (!this.isInitialized) {
      this.$router.push('./')
    } else {
      this.object = this.getFileObject(this.selectedFile.group)
    }
  },
  mounted() {
    if (this.isInitialized) {
      this.updateUpdater(0)
      this.updateContentSavedStatus(true)
      this.freezeUpdater(1)
      this.code = this.currentCode
    }
    this.debouncedValidateFile = _.debounce(this.validateFile, 1000)
  },
  beforeRouteLeave(to, from, next) {
    this.$_handleRoute(next, this.isContentSaved, this.updater)
  },
  beforeRouteUpdate(to, from, next) {
    this.$_handleRoute(next, this.isContentSaved, this.updater)
  },
  computed: {
    ...mapGetters({
      content: 'training/getTrainingContent',
      course: 'training/getTrainingCourse',
      files: 'training/getFilesList',
      getErrors: 'training/getErrors',
      getFileObject: 'training/getFileObject',
      getIsInitialized: 'training/getIsTrainingInitialized',
      isContentSaved: 'contentManager/isContentSaved',
      updater: 'contentManager/getUpdater',
      validationInfo: 'training/getValidationInfo'
    }),
    isInitialized() {
      return this.files && this.getIsInitialized
    },
    errors() {
      return this.getErrors
    },
    canSave() {
      if (this.localErrors.length > 0) {
        return !this.localErrors[0].message.startsWith('Ошибка разбора')
      }
      return true
    },
    currentFile() {
      if (this.files && this.files[this.selectedFile.group]) {
        return this.files[this.selectedFile.group][this.selectedFile.file]
      }
      return ''
    },
    currentLanguage() {
      return this.currentFile.json ? 'json' : 'javascript'
    },
    currentExtension() {
      return this.currentFile.json ? 'json' : 'js'
    },
    currentCode() {
      if (!this.files) {
        return ''
      }
      const file = this.currentFile
      if (file.json) {
        return file.json
      }
      return file
    },
    monacoStyle() {
      return {
        height: this.toggleValidation ? '60vh' : '75vh'
      }
    },
    switchFileTooltip() {
      if (!this.toggleValidation) {
        return 'Включите валидацию для сохранения изменений'
      }
      if (!this.canSave) {
        return 'Невозможно сохранить изменения при ошибке разбора файла'
      }
      return 'Все ОК'
    }
  },
  methods: {
    ...mapMutations({
      freezeUpdater: 'contentManager/freezeUpdater',
      setErrorList: 'training/setErrorList',
      setFile: 'training/setFile',
      updateContentSavedStatus: 'contentManager/updateContentSavedStatus'
    }),
    ...mapActions({
      updateTrainingContent: 'training/updateTrainingContent',
      updateUpdater: 'contentManager/updateUpdater',
      zipCourse: 'training/zipCourse'
    }),
    onClose() {
      this.$router.push(`/content/training/${this.$route.params.trainingId}`)
    },
    onFileChange() {
      this.commitFileChanges()
      this.freezeUpdater(1)
      this.code = this.currentCode
      this.validateFile()
      if (this.currentLanguage === 'json') {
        this.setUpJson()
      }
    },
    updateFile() {
      this.code = this.object.json
      this.validateFile()
    },
    setJsonMap() {
      if (!this.jsonMap) {
        const map = jsonMap.parse(this.code)
        this.jsonMap = map.pointers
        this.jsonData = map.data
      }
    },
    setFileGlyphs() {
      this.$refs.monacoWrapper.setGlyphs([])
      if (this.currentLanguage !== 'json') {
        return
      }
      this.setJsonMap()
      if (
        this.selectedFile.file === 'JSON'
        && this.selectedFile.group === 'Элементы'
      ) {
        const glyphs = []
        for (let i = 0; this.jsonMap[`/items/${i}`] !== undefined; i++) {
          const { value } = this.jsonMap[`/items/${i}`]
          const id = this.jsonMap[`/items/${i}/id`]
          if (value === undefined || id === undefined) {
            continue // eslint-disable-line no-continue
          }
          glyphs.push({
            line: value.line + 1,
            column: value.column,
            id: Number(this.code.substring(id.value.pos, id.valueEnd.pos))
          })
        }
        this.$refs.monacoWrapper.setGlyphs(glyphs)
      }
    },
    onGlyphClicked(glyph) {
      if (!this.canSave) {
        return
      }
      this.commitFileChanges()
      if (
        this.selectedFile.file === 'JSON'
        && this.selectedFile.group === 'Элементы'
      ) {
        this.elementEditId = glyph.id
        this.$bvModal.show('componentEditModal')
      }
    },
    commitFileChanges() {
      if (this.commitParams) {
        if (this.commitParams.language === 'json') {
          this.commitJSON()
        } else if (this.commitParams.language === 'javascript') {
          this.commitJS()
        }
      }
    },
    commitJSON() {
      const parsed = jsonMap.parse(this.code)
      this.commitParams.object.object = parsed.data
      if (this.commitParams.object.code) {
        this.setFile({
          group: this.commitParams.file.group,
          file: 'JavaScript',
          newContent: this.commitParams.object.code.join('\n')
        })
        // this.commitParams.object.importCode(this.commitParams.object.codeAccessor)
      }
      this.setFile({
        group: this.commitParams.file.group,
        file: this.commitParams.file.file,
        newContent: { json: this.code, map: parsed.pointers }
      })
    },
    commitJS() {
      this.commitParams.object.setAttr(
        'code',
        // JSONWrapper.toMultiString(this.code),
        this.code.split('\n'),
      )
      this.commitParams.object.importCode(this.commitParams.object.codeAccessor)
      this.setFile({
        group: this.commitParams.file.group,
        file: this.commitParams.file.file,
        newContent: this.code
      })
      this.setFile({
        group: this.commitParams.file.group,
        file: 'JSON',
        newContent: this.commitParams.object.toMapJSON()
      })
    },
    async onSaveClick() {
      this.isSaving = true
      this.commitFileChanges()
      this.content.contentObject.title = this.course.name
      await this.updateTrainingContent()
      this.updateUpdater(0)
      this.onClose()
    },
    setUpJson() {
      const schemas = this.object.schemas.map((schema) => ({
        uri: schema.uri,
        schema
      }))
      this.$refs.monacoWrapper.setJsonInfo(schemas)
    },
    validateFile() {
      if (this.currentLanguage === 'json') {
        this.localErrors = this.object.validateString(
          this.code,
          this.validationInfo,
        )
        this.setErrorList({
          scope: this.selectedFile.group,
          errors: this.localErrors
        })
      }
      if (this.canSave) {
        this.setFileGlyphs()
      }
      this.errorKey++
    },
    onErrorClicked(error) {
      this.setJsonMap()
      const loc = this.jsonMap[error['Место']]
      if (loc) {
        this.$refs.monacoWrapper.editor.setSelection({
          startColumn: loc.value.column,
          startLineNumber: loc.value.line + 1,
          endColumn: loc.valueEnd.column,
          endLineNumber: loc.valueEnd.line + 1
        })
        this.$refs.monacoWrapper.editor.revealPositionInCenter({
          lineNumber: loc.value.line,
          column: loc.value.column
        })
      }
    },
    onExport() {
      const filename = `${this.selectedFile.group}.${this.currentExtension}`
      const mime = this.currentExtension === 'json'
        ? 'application/json'
        : 'text/javascript'
      const blob = new Blob([this.code], { type: `${mime};charset=utf-8"` })
      saveAs(blob, filename)
    },
    async onZip() {
      this.isZipping = true
      const zip = await this.zipCourse()
      const filename = `${this.content.contentObject.title}.zip`
      saveAs(zip, filename)
      this.isZipping = false
    },
    onImport() {
      this.$bvModal.show('importModal')
    },
    onFileUploaded(content) {
      this.code = content
      this.object.importCode(this.object.codeAccessor)
      this.validateFile()
      this.updateUpdater()
    }
  },
  watch: {
    updater(newUpdater) {
      if (newUpdater > 0) {
        this.updateContentSavedStatus(false)
        window.onbeforeunload = () => 'Имеются несохранённые изменения. Вы точно хотите закрыть страницу?'
      } else {
        this.updateContentSavedStatus(true)
        window.onbeforeunload = undefined
      }
    },
    selectedFile() {
      this.object = this.getFileObject(this.selectedFile.group)
    },
    code() {
      if (this.toggleValidation) {
        this.debouncedValidateFile()
      }
      this.updateUpdater()
      this.jsonMap = null
      this.commitParams = {
        object: this.object,
        language: String(this.currentLanguage),
        file: this.selectedFile
      }
    },
    toggleValidation() {
      this.debouncedValidateFile()
    },
    selectedTheme() {
      this.$cookies.set('editorTheme', this.selectedTheme)
    }
  }
}
</script>

<style>
.editor {
  width: 100%;
  height: 60vh;
}
</style>
